

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "../../components/AppToast";
import CommonDataTable from "../../components/CommonDataTable";
import { fetchRequestCycleCount } from "../../services/cyclecount/cyclecount";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { addTab, setActiveTab } from "../../redux/action/tab.action";

const getRequestStatusRaw = (row) =>
    row.Status ??
    row.status ??
    row.STATUS ??
    row.Request_Status ??
    row.request_status ??
    "";

const pickCycleCountDocId = (row) => {
    const v =
        row.Cycle_Count_Id ??
        row.CycleCount_Id ??
        row.cycle_count_id ??
        row.cycleCountId ??
        row.CycleCountDocId ??
        row.CycleCountHeader_Id ??
        row.CycleCountHeaderId ??
        row.cycleCountHeaderId ??
        row.CycleCount?._id ??
        row.cycleCount?._id ??
        row.CycleCountHeader?._id ??
        row.cycleCountHeader?._id ??
        (typeof row.CycleCount === "string" && row.CycleCount.trim() ? row.CycleCount : undefined) ??
        "";
    return v != null && String(v).trim() !== "" ? String(v).trim() : "";
};

const pickCycleCountKey = (row) =>
    String(
        row.Cycle_Count_Key ??
            row.cyclecountkey ??
            row.Cyclecount_Key ??
            row.CycleCountKey ??
            row.cycle_count_key ??
            row.cycleCountKey ??
            row["Cycle Count Key"] ??
            row.CycleCountHeader?.cyclecountkey ??
            ""
    ).trim();

export default function RequestCycleCountList({ onCreateClick, onView }) {
    const dispatch = useDispatch();
    const { tabs } = useSelector((state) => state.tab);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [allPossibleColumns, setAllPossibleColumns] = useState([]);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    useEffect(() => {
        buildColumns();   // ⭐ important
    }, []);

    useEffect(() => {
        loadData(page, pageSize);
    }, [page, pageSize]);

    const loadData = async (pageNo, size) => {
        const res = await fetchRequestCycleCount(pageNo, size);

        const list = (res?.data?.data || []).map((row) => {
            let date = "";
            if (row.Request_Date) {
                date = /^\d+$/.test(row.Request_Date)
                    ? new Date(Number(row.Request_Date)).toLocaleString()
                    : new Date(row.Request_Date).toLocaleString();
            }

            const statusRaw = getRequestStatusRaw(row);
            const docFromApi = pickCycleCountDocId(row);
            let cycleKey = pickCycleCountKey(row);
            if (!cycleKey && row.cyclecountkey != null) {
                cycleKey = String(row.cyclecountkey).trim();
            }
            if (docFromApi && !cycleKey) {
                cycleKey = String(docFromApi);
            }
            // Prefer Mongo/header id for navigation; otherwise use the business key (View Cycle Count / fetch by id or key)
            const cycleCountNavId = String(docFromApi || cycleKey || "").trim();

            return {
                ...row,
                id: row._id,
                Status: statusRaw !== "" ? statusRaw : row.Status,
                Request_Date_Display: date,
                cyclecountkey: cycleKey,
                cycleCountDocId: cycleCountNavId
            };
        });

        setRows(list);
        setTotal(res?.data?.totalCount || 0);
    };

    const handleDataRefresh = async (payload = {}) => {
        const p = payload.page !== undefined ? payload.page : page;
        const ps = payload.pageSize !== undefined ? payload.pageSize : pageSize;
        await loadData(p, ps);
    };

    /* ✅ ACTION COLUMN */
    const actionsColumn = {
        field: "__actions",
        headerName: "",
        sortable: false,
        width: 50,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchorEl(e.currentTarget);
                    setMenuRow(params.row);
                }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
        )
    };

    const columns = [
        { field: "cyclecountkey", headerName: "Cycle Count Key" },
        { field: "Owner_Start", headerName: "Owner Start" },
        { field: "Owner_End", headerName: "Owner End" },
        { field: "Item_Start", headerName: "Item Start" },
        { field: "Item_End", headerName: "Item End" },
        { field: "Locations_Start", headerName: "Location Start" },
        { field: "Locations_End", headerName: "Location End" },
        { field: "Priority", headerName: "Priority" },
        { field: "User", headerName: "User" },
        { field: "Host_Directed_Count", headerName: "Host Count" },
        { field: "Reference_Num", headerName: "Reference No" },
        { field: "Reference_doc", headerName: "Reference Doc" },
        { field: "Status", headerName: "Status" },
        { field: "Request_Date_Display", headerName: "Request Date" }
    ];

    /* ⭐⭐⭐ CRITICAL PART — SAME AS OTHER TABLES */
    const buildColumns = () => {
        setAllPossibleColumns([
            actionsColumn,
            ...columns
        ]);
    };

    const openAdjustmentTab = () => {
        const path = "/adjustment";
        const existingIndex = tabs.findIndex((tab) => tab.path === path);
        if (existingIndex !== -1) {
            dispatch(setActiveTab(existingIndex));
        } else {
            dispatch(addTab({ title: "Adjustment", path, content: "Adjustment" }));
        }
    };

    const openAppointmentTab = () => {
        const path = "/appointment";
        const existingIndex = tabs.findIndex((tab) => tab.path === path);
        if (existingIndex !== -1) {
            dispatch(setActiveTab(existingIndex));
        } else {
            dispatch(addTab({ title: "Appointment", path, content: "Appointment" }));
        }
    };

     const openLocationTab = () => {
        const path = "/location";
        const existingIndex = tabs.findIndex((tab) => tab.path === path);
        if (existingIndex !== -1) {
            dispatch(setActiveTab(existingIndex));
        } else {
            dispatch(addTab({ title: "Location", path, content: "Location" }));
        }
    };

    return (
        <>
           {/* <div className="d-flex align-items-center gap-3 mb-3">
                    <button className="btn btn-primary me-2" onClick={openAdjustmentTab}>View Adjustment</button>
                    <button className="btn btn-primary" onClick={openAppointmentTab}>View Appointment</button>
                    <button className="btn btn-primary " onClick={openLocationTab}>View Locations</button>
           </div> */}
            <CommonDataTable
                id="request-cyclecount"
                allPossibleColumns={allPossibleColumns}
                initialVisible={[
                    "__actions",
                    ...columns.map(c => c.field)
                ]}
                initialRows={rows}
                rowCount={total}
                page={page}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setPage(0);
                }}
                height={500}
                checkboxSelection={true}
                showCreateButton
                createButtonText="Request"
                onCreateClick={onCreateClick}
                enableExportButton={false}
                enableImportButton={false}
                forInventory={true}
                onDataRefresh={handleDataRefresh}
                linkColumns={{
                    cyclecountkey: {
                        entity: "CycleCount",
                        underline: "always",
                        parseValues: (val, row) => {
                            const key = val != null ? String(val).trim() : "";
                            const navId =
                                row?.cycleCountDocId != null
                                    ? String(row.cycleCountDocId).trim()
                                    : "";
                            if (!key || !navId) return [];
                            return [key];
                        }
                    }
                }}
                onLinkOpen={({ field, row }) => {
                    if (field !== "cyclecountkey") return;
                    const id = row?.cycleCountDocId;
                    if (!id) {
                        toast.error("Cycle count details are not available for this request.");
                        return;
                    }
                    const path = `/import-cycle-count/${encodeURIComponent(id)}`;
                    const existingIndex = tabs.findIndex((tab) => tab.path === path);
                    if (existingIndex !== -1) {
                        dispatch(setActiveTab(existingIndex));
                    } else {
                        dispatch(
                            addTab({
                                title: "View Cycle Count",
                                path,
                                content: "Import_Cycle_Count",
                                componentProps: { initialCycleCountId: id }
                            })
                        );
                    }
                }}
            />

            <Menu
                open={Boolean(menuAnchorEl)}
                anchorEl={menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        onView(menuRow);
                        setMenuAnchorEl(null);
                    }}
                >
                    <i className="bi bi-eye me-2"></i>
                    View
                </MenuItem>
            </Menu>
        </>
    );
}
